import { memo, useEffect } from "react";
import {
  MAX_INLINE_LEVEL,
  commentsStore,
  repliesKey,
  useComment,
  useCommentList,
  useIsExpanded,
  useIsReactionPending,
} from "../../hooks/useCommentsStore";
import CommentRow from "./CommentRow";

const replyCountText = (count) => (count === 1 ? "1 reply" : `${count} replies`);

const controlClass =
  "block py-1 text-xs font-medium text-gray-500 hover:text-gray-800 rounded focus-visible:outline focus-visible:outline-2";

// A comment and, recursively, its direct replies — up to MAX_INLINE_LEVEL
// levels per view. Level 1 is the view's visual root (a post's root comment, or
// the focused comment in a conversation). Comments at the last level don't
// expand inline; they open a focused conversation, which starts a fresh
// three-level window. Reply lists load one page at a time, only once expanded.
//
// `actions` carries the target, the viewer's permissions and callbacks for the
// whole view (see CommentsSection); it is memoized there so branches re-render
// only when their own cache entries change.
function CommentBranch({ commentId, level, alwaysExpanded = false, actions }) {
  const { target } = actions;
  const comment = useComment(target, commentId);
  const expandedFlag = useIsExpanded(target, commentId);
  const key = repliesKey(target, commentId);
  const list = useCommentList(key);
  const isReactionPending = useIsReactionPending(target, commentId);

  const expanded = alwaysExpanded || expandedFlag;
  const canExpandInline = level < MAX_INLINE_LEVEL;

  useEffect(() => {
    if (expanded && canExpandInline) commentsStore.loadFirstPageIfNeeded(key);
    // list.loaded: re-runs after a refresh drops this list while expanded.
  }, [expanded, canExpandInline, key, list.loaded]);

  if (!comment) return null;

  const replyCount = comment.reply_count || 0;
  const isOwn = actions.currentUserId != null && comment.user_id === actions.currentUserId;

  let replies = null;
  if (!canExpandInline) {
    if (replyCount > 0) {
      replies = (
        <button
          type="button"
          onClick={() => actions.onOpenConversation(comment.id)}
          className={controlClass}
        >
          ↳ View conversation · {replyCountText(replyCount)}
        </button>
      );
    }
  } else if (expanded) {
    replies = (
      <>
        {list.ids.map((id) => (
          <MemoCommentBranch key={id} commentId={id} level={level + 1} actions={actions} />
        ))}
        {list.loading ? (
          <p className="py-1 text-xs text-gray-400" role="status">
            Loading replies…
          </p>
        ) : list.error ? (
          <button
            type="button"
            onClick={() =>
              list.loaded ? commentsStore.loadNextPage(key) : commentsStore.loadFirstPageIfNeeded(key)
            }
            className={controlClass}
          >
            Couldn't load replies · Retry
          </button>
        ) : (
          list.next && (
            <button
              type="button"
              onClick={() => commentsStore.loadNextPage(key)}
              className={controlClass}
            >
              Show more replies
            </button>
          )
        )}
        {!alwaysExpanded && list.loaded && (
          <button
            type="button"
            onClick={() => commentsStore.setExpanded(target, comment.id, false)}
            aria-expanded="true"
            className={controlClass}
          >
            Hide replies
          </button>
        )}
      </>
    );
  } else if (replyCount > 0) {
    replies = (
      <button
        type="button"
        onClick={() => commentsStore.setExpanded(target, comment.id, true)}
        aria-expanded="false"
        className={controlClass}
      >
        View {replyCountText(replyCount)}
      </button>
    );
  }

  return (
    <div>
      <CommentRow
        comment={comment}
        isOwn={isOwn}
        canReply={actions.canCreate}
        canInteract={actions.canInteract}
        canEditOwn={actions.canEditOwn}
        canDeleteOwn={actions.canDeleteOwn}
        canRemoveOwn={actions.canRemoveOwn}
        isReactionPending={isReactionPending}
        onReply={() => actions.onReply(comment)}
        onEdit={() => actions.onEdit(comment)}
        onDelete={() => actions.onDelete(comment)}
        onToggleLike={() => actions.onToggleLike(comment)}
        onReact={(type) => actions.onReact(comment, type)}
      />
      {replies && <div className="ml-2.5 pl-3 border-l border-gray-200">{replies}</div>}
    </div>
  );
}

const MemoCommentBranch = memo(CommentBranch);
export default MemoCommentBranch;
